import { useState } from "react";
import { Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Computes the actual end-of-month day for a given month+year.
 * Handles leap years automatically via dayjs.
 */
function getDaysInMonth(year, month) {
  return dayjs(`${year}-${String(month + 1).padStart(2, "0")}-01`).daysInMonth();
}

/**
 * PeriodSelectorField – a visual calendar that auto-selects the
 * first half (1–15) or second half (16–end) when any date is clicked.
 *
 * Integrates with Ant Design Form.Item via value / onChange props.
 * Also calls onPeriodChange(period) so DynamicFormFields can set
 * start_date / end_date hidden fields.
 *
 * period = { month (0-indexed), year, half: "first"|"second", startDate, endDate }
 */
const PeriodSelectorField = ({ value, onChange, onPeriodChange }) => {
  const today = dayjs();
  const [navYear, setNavYear] = useState(value?.year ?? today.year());
  const [navMonth, setNavMonth] = useState(value?.month ?? today.month());

  const selected = value; // { month, year, half, startDate, endDate } or null

  const daysInMonth = getDaysInMonth(navYear, navMonth);

  const handleDayClick = (day) => {
    const half = day <= 15 ? "first" : "second";
    const startDay = half === "first" ? 1 : 16;
    const endDay = half === "first" ? 15 : daysInMonth;

    const pad = (n) => String(n).padStart(2, "0");
    const startDate = `${navYear}-${pad(navMonth + 1)}-${pad(startDay)}`;
    const endDate = `${navYear}-${pad(navMonth + 1)}-${pad(endDay)}`;

    const period = {
      month: navMonth,
      year: navYear,
      half,
      startDate,
      endDate,
    };

    onChange?.(period);
    onPeriodChange?.(period);
  };

  const prevMonth = () => {
    if (navMonth === 0) {
      setNavMonth(11);
      setNavYear((y) => y - 1);
    } else {
      setNavMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (navMonth === 11) {
      setNavMonth(0);
      setNavYear((y) => y + 1);
    } else {
      setNavMonth((m) => m + 1);
    }
  };

  const isSelectedDay = (day) => {
    if (!selected) return false;
    if (selected.month !== navMonth || selected.year !== navYear) return false;
    if (selected.half === "first") return day >= 1 && day <= 15;
    return day >= 16 && day <= daysInMonth;
  };

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const calStyle = {
    display: "inline-block",
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    padding: 12,
    userSelect: "none",
    minWidth: 260,
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    fontWeight: "bold",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
  };

  const dayBase = {
    textAlign: "center",
    padding: "4px 2px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
    lineHeight: "20px",
  };

  const selectedStyle = {
    ...dayBase,
    backgroundColor: "#1677ff",
    color: "#fff",
  };

  const normalStyle = {
    ...dayBase,
    backgroundColor: "#fff",
    color: "#000",
  };

  const hoverColor = "#e6f4ff";

  return (
    <div style={calStyle}>
      <div style={headerStyle}>
        <Button
          size="small"
          type="text"
          icon={<LeftOutlined />}
          onClick={prevMonth}
        />
        <span>
          {MONTH_NAMES[navMonth]} {navYear}
        </span>
        <Button
          size="small"
          type="text"
          icon={<RightOutlined />}
          onClick={nextMonth}
        />
      </div>

      {/* Day-of-week labels */}
      <div style={gridStyle}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 11,
              color: "#888",
              fontWeight: "bold",
            }}
          >
            {d}
          </div>
        ))}

        {/* Empty cells before day 1 */}
        {Array.from(
          { length: dayjs(`${navYear}-${String(navMonth + 1).padStart(2, "0")}-01`).day() },
          (_, i) => (
            <div key={`empty-${i}`} />
          )
        )}

        {days.map((day) => (
          <div
            key={day}
            style={isSelectedDay(day) ? selectedStyle : normalStyle}
            onClick={() => handleDayClick(day)}
            onMouseEnter={(e) => {
              if (!isSelectedDay(day)) e.currentTarget.style.backgroundColor = hoverColor;
            }}
            onMouseLeave={(e) => {
              if (!isSelectedDay(day)) e.currentTarget.style.backgroundColor = "#fff";
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {selected && selected.month === navMonth && selected.year === navYear && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#1677ff",
            textAlign: "center",
          }}
        >
          Selected: {selected.startDate} – {selected.endDate}
        </div>
      )}
    </div>
  );
};

export default PeriodSelectorField;
